import {observable, action, observe} from "mobx";
import {autoSave, loadStore} from "../_core/mobxLocalStorage";
import meService from "../services/meService";
import userService from "../services/userService";
import baseRequest from "../_core/baseRequest";
import {b64EncodeUnicode} from "../utils/helpers";
import appStore from "./appStore";
import i18n from "../i18n";
import {cities} from "../constants/constants";
import {message} from "antd";

class meStore {
    constructor() {
        loadStore(this, "meStore");
        autoSave(this, "meStore");

        // Initialize courierDailyOperations with default values if not loaded from localStorage
        if (!this.courierDailyOperations) {
            this.courierDailyOperations = {
                paymentTypes: {
                    paid: true,
                    creditCard: true,
                    cash: true,
                    ticketRestaurant: true,
                    sodexo: true,
                    multinet: true,
                    setCard: true,
                    yemekmatik: true,
                    metropol: true,
                    cioCard: true,
                    paye: true,
                    winWin: true,
                    tokenFlex: true,
                    unspecified: true
                }
            };
        }

        observe(this, "permissionHash", async change => {
            if (!!change.newValue) {
                await this.getPermissionPolicies();
            }
        });
    }

    @observable map = {
        filters: {
            city: "istanbul",
            courierType: [],
            courierStatus: [],
            company: "",
            subCompany: "",
            courier: "",
            disabled: "all",
            workType: "dedicated"
        },
        settings: {
            center: {lat: 41.11, lng: 29.11},
            zoom: 11
        },
        options: {
            showSubCompanies: false,
            showDummyCouriers: false,
            showRegions: false
        }
    };

    @observable searchTextFilter = "";
    @observable orderPage = {
        filters: {
            orderType: "current",
            deliveryStatusOverrides: [],
            companies: [],
            selectedSubCompanies: [],
            upperGroup: "all",
            regionGroups: [],
            dateRange: []
        }
    };

    @observable courierDailyOperations = undefined;

    @observable isLoggedIn = false;
    @observable fullname = "";
    @observable username = "";
    @observable email = "";
    @observable company = undefined;
    @observable companies = [];
    @observable subcompanies = [];
    @observable sessionToken = undefined;
    @observable userId = undefined;
    @observable role = "";
    @observable auth = undefined;
    @observable permissionHash = undefined;
    @observable permissionPolicies = [];
    @observable isPoolCustomer = false;
    @observable isUatCustomer = false;
    @observable hasCourierDailyOperations = false;

    //cod payment selection kontrolü yapıldı mı?
    @observable hasCodPaymentMethodSelectionChecked = false;

    /**
     * @typedef {object} canOptions Options for the can func.
     * @property {("none"|"message"|"modal"|"redirection")}  responseType Error response type
     * @property {string}  errorContent Error response message content
     * @property {string}  redirectTo Redirection path
     * @param {string} permissionKey Permission key that will be checked.
     * @param {canOptions} options
     */
    @action can = (
        permissionKey,
        {responseType = "none", errorContent = "", redirectTo = undefined} = {}
    ) => {
        const hasPermission = this.permissionPolicies.map(pP => pP.key).includes(permissionKey);
        if (!hasPermission) {
            const errorMessage =
                errorContent || i18n.t("stores.me.permissionError", {ns: "common"});
            switch (responseType) {
                case "message":
                    message.error(errorMessage);
                    break;
                case "modal":
                    appStore.openErrorModal({content: errorMessage});
                    break;
                case "redirection":
                    appStore.setCurrentPath(redirectTo);
                    break;
                default:
                    break;
            }
        }
        return hasPermission;
    };

    @action login = async params => {
        await appStore.setLoading(true);
        this.sessionToken = undefined;
        const token = b64EncodeUnicode(params.username + ":" + params.password);
        baseRequest.addHeader(token);
        return baseRequest
            .post("/login")
            .then(async result => {
                if (result.status !== 200) {
                    appStore.openErrorModal({
                        content: result.data.message
                    });
                    return false;
                } else {
                    if (result.data.authenticated) {
                        this.fullName = result.data.user.fullName;
                        this.phone = result.data.user.phone;
                        this.username = result.data.user.username;
                        this.userId = result.data.user._id;
                        this.email = result.data.user.email;
                        this.company = result.data.user.company;
                        this.companies = result.data.user.companies || [];
                        const subcompanies = result.data.user.subcompanies || [];
                        this.subcompanies = subcompanies;
                        const authSubcompanies = result.data.user?.auth?.subcompanies || [];
                        this.isPoolCustomer =
                            ["cUser"].includes(result.data.user.role) &&
                            subcompanies.length > 0 &&
                            subcompanies
                                .filter(sc => authSubcompanies.includes(sc._id.toString()))
                                .every(subcompany => subcompany.subCompanyWorkType === "pool");
                        this.sessionToken = result.data.sessionToken;
                        this.role = result.data.user.role;
                        this.auth = result.data.user.auth;
                        this.pool = result.data.user.pool;
                        this.isUatCustomer =
                            result.data.user.uat ||
                            process.env.REACT_APP_ENV === "development" ||
                            process.env.NODE_ENV === "development" ||
                            false;
                        this.permissionHash = result.data.user?.permissionHash || "";
                        this.permissionPolicies = result.data.user?.permissionPolicies || [];
                        // TODO: refactor
                        if (!this.auth) {
                            this["auth"] = {};
                        }
                        if (!this.auth?.companies) {
                            this.auth["companies"] = [];
                        }
                        if (!this.auth?.subcompanies) {
                            this.auth["subcompanies"] = [];
                        }
                        if (!this.auth?.cities) {
                            this.auth["cities"] = [];
                        } else if (this.auth?.cities.length > 0) {
                            if (!this.auth?.cities.includes(this.map.filters.city)) {
                                this.map.filters.city = this.auth?.cities[0];
                                console.log(this.map.filters.city);
                                const city = cities.find(x => x.value == this.map.filters.city);
                                console.log(city);
                                this.map.settings.center = city.center;
                            }
                        }
                        baseRequest.addHeader(this.sessionToken);
                        appStore.getCompanyUserList();
                        appStore.getCompanyListFilter();
                        this.hasCodPaymentMethodSelectionChecked = false;
                        this.isLoggedIn = true;

                        // Kurye Günlük İşlemleri sekmesinin görünürlüğü kontrolü
                        if (["cUser"].includes(result.data.user.role) && subcompanies.length > 0) {
                            const result = await import("./stores").then(({default: stores}) =>
                                stores.poolStore.isSubcompanyDedicateIntegration({
                                    subCompanyId: subcompanies[0]._id.toString()
                                })
                            );
                            this.hasCourierDailyOperations = result || false;
                        } else {
                            this.hasCourierDailyOperations = false;
                        }
                    }
                    return result.data.authenticated;
                }
            })
            .finally(() => appStore.setLoading(false));
    };

    @action setCompany = comp => {
        this.company = comp;
    };

    @action logout = () => {
        return baseRequest.post("/logout").then(result => {
            if (result.data.logout) {
                this.resetMeStoreData();
                appStore.resetAppStoreData();
                baseRequest.addHeader(this.sessionToken);
            }
        });
    };

    @action sendResetMail = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.sendResetMail(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action getEmailAndHash = async hash => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.getEmailAndHash(hash);
        await appStore.setLoading(false);
        return resultOfRequest;
    };

    @action resetPassword = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.resetPassword(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.resetPassword.successMessage", {ns: "common"})
        });
        return true;
    };

    @action changePassword = async params => {
        await appStore.setLoading(true);
        // TO DO: authorization control backende eklenince requestte user dönecek, email buradan kaldırılabilir olacak.
        const resultOfRequest = await meService.changePassword(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.changePassword.successMessage", {ns: "common"})
        });
        return true;
    };

    @action changePasswordWithToken = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.changePasswordWithToken(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.changePasswordWithToken.successMessage", {ns: "common"})
        });
        return true;
    };

    @action checkRole = operation => {
        switch (operation) {
            case "isAdmin":
                return ["admin", "superadmin"].includes(this.role);
            case "selectUserRole":
                return ["software", "data", "operationImproving", "admin", "superadmin"].includes(
                    this.role
                );
            case "notVisibleForCompanyUser":
                return !["cUser", "sOperation"].includes(this.role);
            case "isCompanyUser":
                return this.role === "cUser";
            case "updateCourier":
            case "newCourier":
                return ["software", "hr", "admin", "superadmin", "oUser"].includes(this.role);
            case "canAssignForceCourier":
                return this?.auth?.canAssignForceCourier ?? false;
            case "canSplitDeliveries":
                return this?.auth?.canSplitDeliveries ?? false;
            case "listCourierInfo":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "operationSupport",
                    "operationImproving",
                    "software",
                    "data",
                    "cUser",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierWallet":
                return [
                    "admin",
                    "superadmin",
                    "finance",
                    "operationSupport",
                    "operationImproving",
                    "oUser"
                ].includes(this.role);
            case "listCourierLatestStatus":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierHistory":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierDedicationHistory":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierShifts":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierShiftSuggestions":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierOnboarding":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation"
                ].includes(this.role);
            case "listCourierCODInfo":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "listCourierTransferCenterInfo":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "cUser",
                    "oUser",
                    "sOperation",
                    "fieldManager"
                ].includes(this.role);
            case "updateCourierStatus":
                return ["software", "operationSupport", "admin", "superadmin", "oUser"].includes(
                    this.role
                );
            case "assignCourier":
                return ["hr", "software", "admin", "superadmin", "oUser"].includes(this.role);
            case "orderDetail":
                return [
                    "hrEmployment",
                    "operationSupport",
                    "data",
                    "operationImproving",
                    "oUser",
                    "software",
                    "marketing",
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "sOperation"
                ].includes(this.role);
            case "courierDetail":
                return !["marketing", "cUser"].includes(this.role);
            case "updateOrder":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin",
                    "cUser"
                ].includes(this.role);
            case "passOrder":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "updatePreferredCourier":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "transferOrder":
            case "updateForceCourier":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "transferOrder":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "cancelRepeatOrder":
                return [
                    "oUser",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                    //"cUser" TODO: open when resend delivery request is ready for cUser
                ].includes(this.role);
            case "newCompany":
                return ["software", "admin", "superadmin", "oUser"].includes(this.role);
            case "updateCompany":
                return ["software", "admin", "oUser", "superadmin", "cUser"].includes(this.role);
            case "updateCompanyMergeOptions":
                return ["software", "admin", "oUser", "superadmin", "cUser"].includes(this.role);
            case "updateCompanyQueueOptions":
                return ["software", "admin", "oUser", "superadmin", "cUser"].includes(this.role);
            case "updateCompanySlotOptions":
                return ["software", "admin", "oUser", "superadmin", "cUser"].includes(this.role);
            case "newRequest":
                return [
                    "operationSupport",
                    "software",
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "sOperation"
                ].includes(this.role);
            case "userPermission":
                return (
                    ["data", "operationImproving", "software", "admin", "superadmin"].includes(
                        this.role
                    ) ||
                    (this.role === "cUser" && this.company)
                );
            case "isPoolCustomer":
                const userIsCustomer = this.role === "cUser";
                const isPoolCustomer = this.isPoolCustomer;
                return userIsCustomer && isPoolCustomer;
            case "isUatCustomer":
                return this.isUatCustomer;
            case "isUatPoolCustomer":
                return this.isUatCustomer && this.isPoolCustomer;
            case "isCandidateCustomer":
                return (
                    this.isUatCustomer &&
                    //TODO : after db scripts run for all subcompanies, remove the first part("companies") of the condition
                    this.companies.every(company => company?.onboardingStatus === "candidate") &&
                    this.subcompanies.every(
                        subcompany => subcompany?.onboardingStatus === "candidate"
                    )
                );
            case "isNonApiIntegratedCustomer":
                const isCustomer = this.role === "cUser";
                return (
                    isCustomer &&
                    this.companies &&
                    this.companies.every(company => company.isApiIntegrationActive === false)
                );
            case "appConfig":
                return ["superadmin"].includes(this.role);
            case "isOperationUser":
                return ["oUser"].includes(this.role);
            case "isSOperationUser":
                return ["sOperation"].includes(this.role);
            case "createWallet":
                return ["admin", "superadmin"].includes(this.role);
            case "company.assignedCouriers":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.orderMerge":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.orderQueue":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.shiftOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.proofOfDelivery":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.dynamicPricing":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.deliveryVerification":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.shiftSuggestion":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.barcodeOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.cutOffOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.CODPaymentOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.readyOrderNotificationOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "data"
                ].includes(this.role);
            case "company.suppliers":
                return ["admin", "superadmin", "operationSupport"].includes(this.role);
            case "addSubCompany":
                return ["admin", "superadmin", "software", "oUser"].includes(this.role);
            case "changeSubcompanyOnboardingStatus":
                return ["admin", "superadmin", "software", "oUser", "hr", "hrEmployment"].includes(
                    this.role
                );
            case "subcompany.bilingDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "finance"].includes(
                    this.role
                );
            case "subcompany.cariRecordsDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "finance"].includes(
                    this.role
                );
            case "subcompany.creditDetail":
                return ["admin", "superadmin", "operationSupport", "oUser"].includes(this.role);
            case "subcompany.usersDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "finance"].includes(
                    this.role
                );
            case "subcompany.integrationList":
                return ["admin", "superadmin", "operationSupport", "oUser"].includes(this.role);
            case "subcompany.mealCardIntegrations":
                return ["admin", "superadmin", "operationSupport", "oUser"].includes(this.role);
            case "subcompany.salesDetail":
                return ["admin", "superadmin", "operationSupport", "oUser"].includes(this.role);
            case "subcompany.paymentsDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "finance"].includes(
                    this.role
                );
            case "subcompany.vigoPosDetail":
                return ["admin", "superadmin", "operationSupport", "oUser"].includes(this.role);
            case "subcompany.contractsDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "finance"].includes(
                    this.role
                );
            case "createCreditLogObjection":
                return ["admin", "superadmin", "operationSupport", "oUser", "cUser"].includes(
                    this.role
                );
            default:
                return false;
        }
    };

    @action setRole = role => {
        this.role = role;
    };
    @action setPermissionHash = permissionHash => {
        this.permissionHash = permissionHash;
    };

    @action setPermissionPolicies = permissionPolicies => {
        this.permissionPolicies = permissionPolicies;
    };

    @action resetMeStoreData = async () => {
        this.map.options.showSubCompanies = false;
        this.map.options.showDummyCouriers = false;
        this.map.options.showRegions = false;
        this.map.filters.city = "istanbul";
        this.map.settings.center = {lat: 41.11, lng: 29.11};
        this.map.settings.zoom = 11;
        this.map.filters.courierType = [];
        this.map.filters.courierStatus = [];
        this.map.filters.company = "";
        this.map.filters.subCompany = "";
        this.map.filters.courier = "";

        this.searchTextFilter = "";
        this.orderPage = {
            filters: {
                orderType: "current",
                deliveryStatusOverrides: [],
                companies: [],
                selectedSubCompanies: [],
                upperGroup: "all",
                regionGroups: [],
                dateRange: []
            }
        };

        // courierDailyOperations is user preference, should not be reset on logout
        // this.courierDailyOperations = { ... }

        this.isLoggedIn = false;
        this.fullname = "";
        this.fullName = "";
        this.phone = "";
        this.username = "";
        this.email = "";
        this.company = undefined;
        this.sessionToken = undefined;
        this.userId = undefined;
        this.role = "";
        this.auth = {companies: [], subcompanies: [], cities: []};
        this.permissionHash = undefined;
        this.permissionPolicies = [];
        this.hasCodPaymentMethodSelectionChecked = false;
    };

    @action getPermissionPolicies = async () => {
        await appStore.setLoading(true);
        const resultOfRequest = await userService.getCurrentUsersPermissionPolicies();
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        const {permissionPolicies = []} = resultOfRequest.data;
        this.setPermissionPolicies(permissionPolicies);
        return resultOfRequest.data;
    };

    @action setHasCodPaymentMethodSelectionChecked = () => {
        this.hasCodPaymentMethodSelectionChecked = true;
    };

    @action setCourierDailyOperationsPaymentTypes = paymentTypes => {
        this.courierDailyOperations.paymentTypes = paymentTypes;
    };
}
export default new meStore();
